import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import {
    DELETE_LTI_CREDENTIAL,
    GET_LTI_CREDENTIAL_LIST,
    REGENERATE_SHARED_SECRET,
    UPSERT_LTI_CREDENTIAL
} from '../../config/apiPaths';
import { type LTICredential } from '../../types/apiTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls } from '../../utils/url';

export enum IntegrationsView {
    List = 0,
    Edit = 1,
    Create = 2
}

export enum LTIVersion {
    V1_1 = '1.1',
    V1_3 = '1.3',
    NONE = 'none'
}


const useAdminIntegrations = () => {
    const { integration } = useParams<{ integration: string }>();

    const { uuid, isLoading } = useContainer(AdminAuthContainer);
    const { api } = getUrls();
    const { t } = useTranslation('admin_settings');
    const [integrations, setIntegrations] = useState<LTICredential[]>([]);
    const [integrationsFetchStatus, setIntegrationsFetchStatus] = useState<boolean>(false);
    const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
    const [integrationId, setIntegrationId] = useState<string | null>(null);
    const [view, setView] = useState<IntegrationsView>(IntegrationsView.List);

    const { enqueueSnackbar } = useSnackbar();
    const fetchIntegrationList = useCallback(async () => {
        try {
            const { data } = await axios.get<LTICredential[]>(`${api}${GET_LTI_CREDENTIAL_LIST}`, {
                headers: getUserUuidHeaders(uuid)
            });
            setIntegrations(data);
            setIntegrationsFetchStatus(true);
            if (integration) {
                const integrationToSelect = data.find((i) => i.lti_creds_uuid === integration );
                setIntegrationId(integration);
                setSelectedIntegration(integrationToSelect);
            }
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.fetch-all-credentials'));
        }
    }, [api, uuid, t, enqueueSnackbar, setIntegrationId, setSelectedIntegration, integration]);

    const updateIntegration = async (recordData: Partial<LTICredential>) => {
        try {
            const { data } = await axios.post<LTICredential>(`${api}${UPSERT_LTI_CREDENTIAL}`, {
                ...recordData,
                user_uuid: uuid,
                learn_app_key: recordData.learn_app_key || null,
                learn_app_secret: recordData.learn_app_secret || null
            });
            const successMessage = t('integration-success.update', { title: recordData.title });
            enqueueSnackbar(successMessage, { variant: 'success' });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.update-integration'));
        }
    };

    const createIntegration = async (recordData: Partial<LTICredential>) => {
        try {
            const { data } = await axios.post<LTICredential>(`${api}${UPSERT_LTI_CREDENTIAL}`, {
                ...recordData,
                user_uuid: uuid,
                learn_app_key: recordData.learn_app_key || null,
                learn_app_secret: recordData.learn_app_secret || null
            });
            const successMessage = t('integration-success.create', { title: recordData.title });
            enqueueSnackbar(successMessage, { variant: 'success' });

            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.create-integration'));
        }
    };

    const regenerateSecret = async () => {
        try {
            const { data } = await axios.post(`${api}${REGENERATE_SHARED_SECRET}`, {
                lti_creds_uuid: selectedIntegration?.lti_creds_uuid,
                user_uuid: uuid
            });

            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.regenerate-secret'));
        }
    };

    const deleteIntegration = async () => {
        try {
            const { data } = await axios.delete(
                `${api}${DELETE_LTI_CREDENTIAL}?lti_creds_uuid=${selectedIntegration?.lti_creds_uuid}`,
                { headers: getUserUuidHeaders(uuid) }
            );

            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.delete-integration'));
        }
    };

    useEffect(() => {
        if (!isLoading && uuid) {
            fetchIntegrationList();
        }
    }, [view, fetchIntegrationList, isLoading, uuid]);

    return {
        isLoading,
        fetchIntegrationList,
        integrations,
        integrationsFetchStatus,
        selectedIntegration,
        setSelectedIntegration,
        updateIntegration,
        createIntegration,
        deleteIntegration,
        regenerateSecret,
        view,
        setView,
        uuid,
        integrationId,
        setIntegrationId
    };
};

const AdminIntegrationsContainer = createContainer(useAdminIntegrations);

export default AdminIntegrationsContainer;
